import * as React from 'react'
import { Link, } from 'react-router-dom'

import ActivityPackHeader from './activityPackHeader'
import {
  Recommendation,
  Student,
} from './interfaces'
import {
  DEFAULT_LEFT_PADDING,
  DEFAULT_LEFT_PADDING_FOR_MOBILE,
  MOBILE_WIDTH,
  correctImage,
  noDataYet,
  RecommendedGlyphTooltip,
  accountIcon,
} from './shared'

import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths'
import useStickyTableHeader from '../../../../../Shared/hooks/useStickyTableHeader'
import {
  DataTable,
  Checkbox,
  DataTableChip,
} from '../../../../../Shared/index'

const TABLE_WIDTH_PROPS = {
  maxTableWidth: 1910, // ten columns at their 191px minimum, so the widest stock diagnostic fits exactly at the cap
  minTableWidth: 963,
  additionalWidthRestrictions: 10 // 8px of data table row padding + the wrapper's 1px borders
}
const MIN_COLUMN_WIDTH = 191

const noop = () => {}

interface RecommendationsTableProps {
  recommendations: Recommendation[];
  responsesLink: (studentId: number) => string;
  previouslyAssignedRecommendations: Recommendation[];
  students: Student[];
  selections: Recommendation[];
  setSelections: (selections: Recommendation[]) => void;
  studentsWhoCompletedDiagnostic: Student[];
}

interface RecommendationCellProps {
  student: Student;
  isAssigned: boolean;
  isRecommended: boolean;
  isSelected: boolean;
  setSelections: (selections: Recommendation[]) => void;
  selections: Recommendation[];
  selectionIndex: number;
  completedCount?: number;
  activityCount?: number;
}

const RecommendationCell = ({ student, isAssigned, isRecommended, isSelected, setSelections, selections, selectionIndex, activityCount, completedCount, }: RecommendationCellProps) => {
  const [showAssignedText, setShowAssignedText] = React.useState(false)

  React.useEffect(() => {
    if (isAssigned) {
      setShowAssignedText(true)
      setTimeout(() => { setShowAssignedText(false)}, 5000)
    }
  }, [isAssigned])

  function toggleSelection() {
    const newSelections = [...selections];
    if (isSelected) {
      newSelections[selectionIndex].students = newSelections[selectionIndex].students.filter(s => s !== student.id)
    } else {
      newSelections[selectionIndex].students.push(student.id);
    }
    setSelections(newSelections)
  }

  let ariaLabel = `Select ${student.name} for pack assignment`
  const checkbox = (<Checkbox selected={isSelected} />)
  const assignedText = showAssignedText && <div className="assigned"><span>Assigned</span></div>
  const progressIndicator = isAssigned && <p className="progress-indicator"><span className="counts">{completedCount} of {activityCount}</span><span>Activities Completed</span></p>

  if (isSelected) {
    ariaLabel = `Unselect ${student.name} for pack assignment`
  }

  if (isAssigned) {
    ariaLabel = `${student.name} has been assigned this pack`
  }

  return (
    <div className="recommendation-cell">
      <button aria-label={ariaLabel} className={`interactive-wrapper focus-on-light ${isRecommended && !showAssignedText ? 'recommended' : ''}`} onClick={isAssigned ? noop : toggleSelection} type="button">
        {isAssigned && correctImage}
        {assignedText || progressIndicator || checkbox}
        {isRecommended ? <RecommendedGlyphTooltip /> : <span />}
        <span />
      </button>
    </div>
  )
}


const RecommendationsTable = ({ recommendations, responsesLink, students, selections, previouslyAssignedRecommendations, setSelections, studentsWhoCompletedDiagnostic, }: RecommendationsTableProps) => {
  const tableRef = React.useRef<HTMLTableElement>(null);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLElement>(null);

  const columnCount = 1 + (recommendations?.length || 0)
  const columnSizing = React.useMemo(
    () => Array(columnCount).fill({ width: TABLE_WIDTH_PROPS.maxTableWidth / columnCount, minWidth: MIN_COLUMN_WIDTH }),
    [columnCount]
  )

  const columnWidths = useScaledTableColumnWidths({
    columnWidths: columnSizing,
    containerRef,
    ...TABLE_WIDTH_PROPS,
  });
  const columnWidth = Object.values(columnWidths)[0]

  const tableHasContent = studentsWhoCompletedDiagnostic.length;

  useStickyTableHeader({
    wrapperRef: tableWrapperRef,
    tableRef: tableRef,
    enabled: tableHasContent > 0,
    dependencies: [Object.values(columnWidths).join('|'), recommendations?.length || 0],
  });

  function paddingLeft() {
    if (MOBILE_WIDTH >= window.innerWidth) { return DEFAULT_LEFT_PADDING_FOR_MOBILE }

    return DEFAULT_LEFT_PADDING
  }

  function onSelectAllColumnClick(activityPackId: number) {
    const newSelections = [...selections];
    const selectionIndex = newSelections.findIndex(sel => sel.activity_pack_id === activityPackId)
    const studentIdsWhoHaveCompletedDiagnostic = students.filter(s => s.completed).map(s => s.id)

    newSelections[selectionIndex].students = studentIdsWhoHaveCompletedDiagnostic

    setSelections(newSelections)
  }

  // Build headers for DataTable
  const headers = React.useMemo(() => {
    const nameHeader = {
      attribute: 'name',
      name: 'Name',
      renderCellsAsHeaders: true,
      headerClassName: 'name-cell',
      rowSectionClassName: 'name-cell',
      width: columnWidth,
      noTooltip: true
    };

    const recommendationHeaders = recommendations?.map((recommendation, index) => {
      const { activity_pack_id, name, activity_count } = recommendation;

      function handleSelectAllClick() {
        onSelectAllColumnClick(activity_pack_id)
      }

      return {
        ariaLabel: name,
        attribute: `recommendation_${activity_pack_id}`,
        name: (
          <ActivityPackHeader
            activityCount={activity_count}
            activityPackId={activity_pack_id}
            handleSelectAllClick={handleSelectAllClick}
            key={name}
            name={name}
            style={{}}
          />
        ),
        width: columnWidth,
      };
    }) || [];

    return [nameHeader, ...recommendationHeaders];
  }, [recommendations, onSelectAllColumnClick, columnWidth]);

  // Build rows for DataTable
  const rows = React.useMemo(() => {
    return students.map(student => {
      const { name, completed, id } = student;

      let nameCell = (
        <div>
          <DataTableChip color="grey" icon={accountIcon} label={name} />
          <span className="name-section-subheader">Diagnostic not completed</span>
        </div>
      );

      const rowData = { id: id || name, name: nameCell };

      if (completed) {
        nameCell = (
          <Link className="focus-on-light" rel="noopener noreferrer" target="_blank" to={responsesLink(id)}>
            <DataTableChip color="grey" icon={accountIcon} label={name} />
          </Link>
        );
        rowData.name = nameCell;

        // Add recommendation cells
        selections.forEach((selection: Recommendation, i: number) => {
          const previouslyAssignedRecommendation = previouslyAssignedRecommendations.find((r: Recommendation) => r.activity_pack_id === selection.activity_pack_id);
          const isAssigned = previouslyAssignedRecommendation?.students.includes(id) || false;
          const completedCount = isAssigned && previouslyAssignedRecommendation?.diagnostic_progress?.[id];
          const isRecommended = recommendations.find((r: Recommendation) => r.activity_pack_id === selection.activity_pack_id)?.students.includes(id) || false;
          const isSelected = selection.students.includes(id);

          rowData[`recommendation_${selection.activity_pack_id}`] = (
            <RecommendationCell
              activityCount={previouslyAssignedRecommendation?.activity_count || 0}
              completedCount={completedCount}
              isAssigned={isAssigned}
              isRecommended={isRecommended}
              isSelected={isSelected}
              key={`${id}-${selection.activity_pack_id}`}
              selectionIndex={i}
              selections={selections}
              setSelections={setSelections}
              student={student}
            />
          );
        });
      } else {
        // Add empty cells for incomplete students
        selections.forEach((selection: Recommendation) => {
          rowData[`recommendation_${selection.activity_pack_id}`] = <div />;
        });
      }

      return rowData;
    });
  }, [students, selections, recommendations, previouslyAssignedRecommendations, responsesLink, setSelections]);

  if (!tableHasContent) {
    return (
      <div style={{ marginLeft: paddingLeft() }}>
        <div className="no-data-yet">{noDataYet}</div>
      </div>
    );
  }

  return (
    <section ref={containerRef}>
      <div className="individual-recommendations-table-wrapper" ref={tableWrapperRef}>
        <DataTable
          className="recommendations-table"
          headers={headers}
          reportingFormat={true}
          rows={rows}
          tableAriaLabel="Independent practice recommendations by student"
          tableRef={tableRef}
        />
      </div>
    </section>
  );
}

export default RecommendationsTable
